import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { getCompanyCamProject, getCompanyCamProjectList } from "@/network/network";
import { debounce } from "@/other/utils/otherUtils";
import { showToast } from "@/other/utils/utils";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useHasCompanyCamKey } from "@/react/hooks/useHasCompanyCamKey";
import { EntityRecord } from "@/types/entity";
import { getCompanyCamErrorMessage } from "@/integrations";
import { queryClient } from "@/lib/queryClient";

/** Bumped on successful CC link so Media/Documents can show an importing indicator. */
export const companyCamLinkSignalKey = (entityType: string, entityId: number) =>
  ["companycam-just-linked", entityType, entityId] as const;

type Address = {
  city?: string;
  country?: string;
  postal_code?: string;
  state?: string;
  street_address_1?: string;
  street_address_2?: string;
};

export interface ICCProject {
  id: number;
  name: string;
  address: Address;
  imageUrl: string;
  creatorName: string;
  updatedAt: string;
}

export interface ICCProjectApiData {
  id: number;
  name: string;
  address: Address;
  feature_image: { url: string }[];
  creator_name: string;
  updated_at: string;
  photo_count?: number;
}

const COMPANY_CAM_PROJECT_LIST_PER_PAGE = 25;

export const useSwivuCompanyCam = ({ record }: { record: EntityRecord }) => {
  const { project, selectedBoard } = useSwivuBryntumContext();
  const hasCompanyCamApiKey = useHasCompanyCamKey();
  const boardId = selectedBoard?.id;

  const [searchQuery, setSearchQuery] = useState("");
  const [companyCamError, setCompanyCamError] = useState<string | null>(null);

  const clearCompanyCamError = useCallback(() => setCompanyCamError(null), []);

  const captureCompanyCamError = useCallback((error: unknown, fallback: string) => {
    const message = getCompanyCamErrorMessage(error, fallback);
    setCompanyCamError(message);
    return message;
  }, []);

  // Current project (auto-fetched from record)
  const {
    data: currentCCProject,
    isFetching: isFetchingCurrentProject,
    refetch: fetchCurrentProject,
    error: currentProjectError,
  } = useQuery({
    queryKey: ["ccProject", record.entityType, record.id, hasCompanyCamApiKey, boardId, record.companyCamProject],
    queryFn: async () => {
      const response = await getCompanyCamProject(boardId, record.companyCamProject);
      if (!response.data) throw new Error(`HTTP error! status: ${response.status}`);
      return response.data as ICCProjectApiData;
    },
    enabled: !!record.companyCamProject && hasCompanyCamApiKey && !!boardId,
    staleTime: 5 * 60_000,
  });

  // Project list with infinite scroll
  const {
    data: ccProjectsData,
    fetchNextPage: fetchMoreProjects,
    hasNextPage: hasMoreProjects,
    isFetching: isLoadingProjects,
    refetch: getCompanyCamProjects,
    error: projectListError,
  } = useInfiniteQuery({
    queryKey: ["ccProjectList", boardId, hasCompanyCamApiKey, searchQuery],
    queryFn: async ({ pageParam }) => {
      const response = await getCompanyCamProjectList(boardId, {
        page: pageParam,
        query: searchQuery,
      });
      const data: ICCProjectApiData[] = response.data || [];
      return data.map((proj) => ({
        id: proj.id,
        name: proj.name,
        address: proj.address,
        imageUrl: proj.feature_image?.[2]?.url || "https://ps.w.org/replace-broken-images/assets/icon-256x256.png",
        creatorName: proj.creator_name,
        updatedAt: proj.updated_at,
      }));
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.length === COMPANY_CAM_PROJECT_LIST_PER_PAGE ? (lastPageParam as number) + 1 : undefined,
    enabled: hasCompanyCamApiKey && !!boardId,
    staleTime: 2 * 60_000,
  });

  useEffect(() => {
    const error = currentProjectError || projectListError;
    if (error) {
      captureCompanyCamError(error, "Failed to load CompanyCam projects.");
    }
  }, [currentProjectError, projectListError, captureCompanyCamError]);

  useEffect(() => {
    if (!hasCompanyCamApiKey) {
      queryClient.removeQueries({ queryKey: ["ccProjectList", boardId] });
      queryClient.removeQueries({ queryKey: ["ccProject"] });
      clearCompanyCamError();
    }
  }, [hasCompanyCamApiKey, boardId, clearCompanyCamError]);

  const ccProjects = ccProjectsData?.pages.flat() ?? [];

  // Link project mutation
  const linkProjectMutation = useMutation({
    mutationFn: async (ccProjectId: number) => {
      const previous = record.companyCamProject;
      record.companyCamProject = ccProjectId;
      try {
        await project.sync();
      } catch (error) {
        record.companyCamProject = previous;
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.setQueryData(
        companyCamLinkSignalKey(record.entityType, Number(record.id)),
        Date.now(),
      );
      showToast({ msg: "CompanyCam project linked.", type: "success", duration: 3000 });
    },
    onError: (error) => {
      captureCompanyCamError(error, "Failed to link project.");
      showToast({ msg: "Failed to link project.", type: "error", duration: 3000 });
    },
  });

  // Unlink project mutation
  const unlinkProjectMutation = useMutation({
    mutationFn: async () => {
      const previous = record.companyCamProject;
      record.companyCamProject = null;
      try {
        await project.sync();
      } catch (error) {
        record.companyCamProject = previous;
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.setQueryData(
        companyCamLinkSignalKey(record.entityType, Number(record.id)),
        0,
      );
      showToast({ msg: "CompanyCam project unlinked.", type: "success", duration: 3000 });
    },
    onError: () => showToast({ msg: "Failed to unlink project.", type: "error", duration: 3000 }),
  });

  // Debounced search — changing searchQuery invalidates the infinite query via queryKey
  const onSearch = useMemo(
    () => debounce((query: string) => setSearchQuery(query), 300),
    []
  );

  const retryCompanyCam = useCallback(async () => {
    clearCompanyCamError();
    await fetchCurrentProject();
  }, [clearCompanyCamError, fetchCurrentProject]);

  return {
    // Current project
    currentCCProject,
    isFetchingCurrentProject,
    fetchCurrentProject,

    // Project list
    ccProjects,
    fetchMoreProjects,
    hasMoreProjects,
    isLoadingProjects,
    onSearch,
    getCompanyCamProjects,

    // Project operations
    setCompanyCamProject: linkProjectMutation.mutate,
    unsetCompanyCamProject: unlinkProjectMutation.mutate,

    // Key status
    hasCompanyCamApiKey,

    // Errors
    companyCamError,
    clearCompanyCamError,
    retryCompanyCam,
  };
};
